import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { TemplatePreviewModalProvider } from "./TemplatePreviewModalContext";
import TemplatePreviewModal from "./TemplatePreviewModal";
import apiRequest from "../../utils/apiRequest";

const EVENT_NAME = "openTemplatePreviewModal";

const TemplatePreviewModalWrapper = (props) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [templateData, setTemplateData] = useState(null);
  const [loading, setLoading] = useState(false);

  const {
    website,
    subscription,
    account,
    current_user,
    remix_mappings,
    styles = {},
    createEndpoint,
    redirectPath,
  } = props;

  const [brandStyle, setBrandStyle] = useState({});
  const [remixPrompt, setRemixPrompt] = useState("");
  const [detectedAssetTheme, setDetectedAssetTheme] = useState(null);

  useEffect(() => {
    const handleOpen = async (event) => {
      const {
        templateId,
        objectId,
        ctaType,
        brandStyle: eventBrandStyle,
        remixPrompt: eventRemixPrompt,
        detectedAssetTheme: eventDetectedTheme,
      } = event.detail || {};

      if (!templateId || !website) return;

      const urlMatch = window.location.pathname.match(/\/websites\/(\d+)/);
      const urlWebsiteId = urlMatch ? parseInt(urlMatch[1], 10) : null;
      if (urlWebsiteId && website.id && urlWebsiteId !== website.id) return;

      const remixPromptFromUrl =
        new URLSearchParams(window.location.search).get("remix_prompt") || "";
      const effectiveRemixPrompt = eventRemixPrompt || remixPromptFromUrl || "";

      const mergedBrandStyle = eventDetectedTheme
        ? { ...(eventBrandStyle || {}), ...eventDetectedTheme }
        : eventBrandStyle || {};
      setBrandStyle(mergedBrandStyle);
      setRemixPrompt(effectiveRemixPrompt);
      setDetectedAssetTheme(eventDetectedTheme || null);

      setLoading(true);
      setModalOpen(true);

      try {
        const response = await apiRequest(
          "GET",
          `${window.location.origin}/templates/${templateId}`,
          null,
          null,
          { Accept: "application/json" }
        );

        if (response?.code >= 200 && response?.code < 300 && response?.json) {
          setTemplateData(response.json);
        } else {
          setModalOpen(false);
        }
      } catch (err) {
        setModalOpen(false);
      } finally {
        setLoading(false);
      }
    };

    const handleOpenWithGuard = (event) => {
      event.stopImmediatePropagation();
      handleOpen(event);
    };
    document.addEventListener(EVENT_NAME, handleOpenWithGuard, true);
    return () =>
      document.removeEventListener(EVENT_NAME, handleOpenWithGuard, true);
  }, [website]);

  const handleClose = () => {
    setModalOpen(false);
    setTemplateData(null);
    setRemixPrompt("");
    setDetectedAssetTheme(null);
  };

  const showShare = ![
    "step_templates",
    "section_templates",
    "element_templates",
    "field_templates",
  ].includes(document.body?.getAttribute("data-controller") || "");

  const modalContent =
    modalOpen && templateData ? (
      <TemplatePreviewModalProvider
        templateData={templateData}
        website={website}
        subscription={subscription}
        account={account}
        current_user={current_user}
        remix_mappings={remix_mappings}
        styles={{}}
        ctaType={templateData.object?.cta_type}
        remixPrompt={remixPrompt}
        detectedAssetTheme={detectedAssetTheme}
      >
        <TemplatePreviewModal
          templateData={templateData}
          onClose={handleClose}
          createEndpoint={createEndpoint}
          redirectPath={redirectPath}
          brandStyle={brandStyle}
          showShare={showShare}
        />
      </TemplatePreviewModalProvider>
    ) : null;

  return (
    <>
      {modalOpen && loading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10001]">
          <span className="cf-loading-spinner" />
        </div>
      )}
      {modalContent && createPortal(modalContent, document.body)}
    </>
  );
};

export default TemplatePreviewModalWrapper;
export { EVENT_NAME };
